// SPDX-License-Identifier: GPL-3.0-or-later
// Eyes and mouths of the pinball SigmaBot, built from three.js primitives.
// Own code. The upstream 3D bot takes these parts from a binary parts file (a GLB). The table has
// no such file, so each part is a few primitives in the shape of the 2D part of the upstream
// renderer (web/bots/parts-face.ts). The same spec name gives the same shape.
// Rules: no clock, no random. Body units: up is +y, the front is +z.

import {
  BoxGeometry,
  CircleGeometry,
  ConeGeometry,
  Group,
  OctahedronGeometry,
  SphereGeometry,
  TorusGeometry,
} from "three";
import type { BodyDef } from "./bodies.ts";
import { CREAM, GOLD, INK, MOUTH_DARK, PINK, type Rgba } from "./kit.ts";
import { frontZ } from "./loft.ts";
import { part } from "./parts.ts";
import type { SigmaBotBody, SigmaBotEyes, SigmaBotMouth } from "./spec.ts";

/** What an eye or mouth drawer needs: the parent group, the unit size and the local front z. */
interface Draw {
  readonly g: Group;
  /** The unit size: the eye radius of the body. */
  readonly r: number;
  /** Local z where marks sit (in front of the eye white or the body). */
  readonly z: number;
}

const dot = (d: Draw, rad: number, x = 0, y = 0, color: Rgba = INK): void => {
  part(d.g, new SphereGeometry(rad, 8, 6), color, x, y, d.z, { sz: 0.5 });
};

const bar = (d: Draw, len: number, x: number, y: number, rz = 0, color: Rgba = INK): void => {
  part(d.g, new BoxGeometry(len, d.r * 0.28, d.r * 0.3), color, x, y, d.z, { rz });
};

/** An arc of a circle. `up` is a hill shape. Not up is a cup shape. */
const arc = (d: Draw, radius: number, x: number, y: number, up: boolean, rz = 0): void => {
  part(d.g, new TorusGeometry(radius, d.r * 0.14, 4, 8, Math.PI), INK, x, y, d.z, {
    rz: (up ? 0 : Math.PI) + rz,
  });
};

const ring = (d: Draw, radius: number, x = 0, y = 0, color: Rgba = INK): void => {
  part(d.g, new TorusGeometry(radius, d.r * 0.13, 4, 12), color, x, y, d.z);
};

type EyeStyle = (d: Draw, side: -1 | 1) => void;

/** Each eye style: the mark that goes on the eye white. Local unit is `d.r`. */
const EYE_STYLES: Record<SigmaBotEyes, EyeStyle> = {
  dot: (d) => dot(d, d.r * 0.55),
  wide: (d) => dot(d, d.r * 0.75),
  sleepy: (d) => {
    dot(d, d.r * 0.4, 0, -d.r * 0.2);
    bar(d, d.r * 1.7, 0, d.r * 0.15);
  },
  spark: (d) => {
    dot(d, d.r * 0.62);
    dot(d, d.r * 0.2, d.r * 0.25, d.r * 0.25, CREAM);
  },
  wink: (d, side) => (side < 0 ? arc(d, d.r * 0.6, 0, -d.r * 0.2, true) : dot(d, d.r * 0.55)),
  angry: (d, side) => {
    dot(d, d.r * 0.5, 0, -d.r * 0.15);
    bar(d, d.r * 1.8, 0, d.r * 0.75, -side * 0.5);
  },
  heart: (d) => {
    dot(d, d.r * 0.38, -d.r * 0.3, d.r * 0.2, PINK);
    dot(d, d.r * 0.38, d.r * 0.3, d.r * 0.2, PINK);
    part(d.g, new ConeGeometry(d.r * 0.66, d.r * 0.9, 8), PINK, 0, -d.r * 0.25, d.z, {
      rz: Math.PI,
      sz: 0.5,
    });
  },
  x: (d) => {
    bar(d, d.r * 1.5, 0, 0, Math.PI / 4);
    bar(d, d.r * 1.5, 0, 0, -Math.PI / 4);
  },
  happy: (d) => arc(d, d.r * 0.6, 0, -d.r * 0.2, true),
  sad: (d, side) => {
    dot(d, d.r * 0.5, 0, -d.r * 0.2);
    bar(d, d.r * 1.6, 0, d.r * 0.8, side * 0.35);
  },
  surprised: (d) => {
    ring(d, d.r * 0.7);
    dot(d, d.r * 0.2);
  },
  dizzy: (d) => {
    ring(d, d.r * 0.7);
    ring(d, d.r * 0.35);
  },
  star: (d) =>
    part(d.g, new OctahedronGeometry(d.r * 0.85), GOLD, 0, 0, d.z, { sz: 0.4, rz: Math.PI / 4 }),
  lashes: (d, side) => {
    dot(d, d.r * 0.55);
    for (let i = 0; i < 3; i++) {
      const a = side * (0.5 + i * 0.4);
      bar(d, d.r * 0.8, side * d.r * (0.7 + i * 0.1), d.r * (0.5 + i * 0.25), a);
    }
  },
  smug: (d) => {
    dot(d, d.r * 0.5, 0, -d.r * 0.25);
    bar(d, d.r * 1.8, 0, d.r * 0.25);
  },
  side: (d, side) => dot(d, d.r * 0.5, side * d.r * 0.35),
};

/** Add both eyes to the face group. Each eye is a flat cream ball with a mark in front. */
export function addEyes(face: Group, kind: SigmaBotBody, b: BodyDef, style: SigmaBotEyes): void {
  const y = -b.faceY;
  for (const side of [-1, 1] as const) {
    const x = side * b.eyeDx;
    const eye = new Group();
    eye.name = side < 0 ? "SigmaBotEyeL" : "SigmaBotEyeR";
    eye.position.set(x, y, frontZ(kind, y, x));
    part(eye, new SphereGeometry(b.eyeR, 10, 6), CREAM, 0, 0, 0, { sz: 0.5 });
    EYE_STYLES[style]({ g: eye, r: b.eyeR, z: b.eyeR * 0.55 }, side);
    face.add(eye);
  }
}

type MouthStyle = (d: Draw) => void;

const half = (d: Draw, w: number, color: Rgba): void => {
  part(d.g, new CircleGeometry(w, 12, Math.PI, Math.PI), color, 0, 0, d.z);
};

const filled = (d: Draw, w: number, h: number, color: Rgba): void => {
  part(d.g, new SphereGeometry(1, 10, 8), color, 0, 0, d.z, { sx: w, sy: h, sz: 0.25 });
};

/** Each mouth style. Local unit is `d.r`. The half width of a mouth is about 1.4 units. */
const MOUTH_STYLES: Record<SigmaBotMouth, MouthStyle> = {
  smile: (d) => arc(d, d.r * 1.1, 0, d.r * 0.6, false),
  grin: (d) => {
    half(d, d.r * 1.3, MOUTH_DARK);
    arc(d, d.r * 1.3, 0, 0, false);
  },
  flat: (d) => bar(d, d.r * 2.2, 0, 0),
  open: (d) => filled(d, d.r * 0.9, d.r * 0.7, MOUTH_DARK),
  tongue: (d) => {
    filled(d, d.r * 0.9, d.r * 0.7, MOUTH_DARK);
    part(d.g, new SphereGeometry(d.r * 0.45, 8, 6), PINK, 0, -d.r * 0.35, d.z + 0.2, { sz: 0.4 });
  },
  fang: (d) => {
    bar(d, d.r * 2.2, 0, 0);
    for (const s of [-1, 1]) {
      part(d.g, new ConeGeometry(d.r * 0.3, d.r * 0.7, 6), CREAM, s * d.r * 0.7, -d.r * 0.4, d.z, {
        rz: Math.PI,
      });
    }
  },
  wavy: (d) => {
    for (let i = 0; i < 5; i++)
      dot(d, d.r * 0.2, (i - 2) * d.r * 0.5, (i % 2 ? -1 : 1) * d.r * 0.2);
  },
  frown: (d) => arc(d, d.r * 1.1, 0, -d.r * 0.4, true),
  smirk: (d) => arc(d, d.r * 0.9, d.r * 0.3, d.r * 0.4, false, 0.25),
  oh: (d) => ring(d, d.r * 0.55),
  kiss: (d) => dot(d, d.r * 0.45, 0, 0, PINK),
  teeth: (d) => {
    filled(d, d.r * 1.1, d.r * 0.6, MOUTH_DARK);
    part(d.g, new BoxGeometry(d.r * 1.6, d.r * 0.35, d.r * 0.2), CREAM, 0, d.r * 0.2, d.z + 0.2);
  },
  cat: (d) => {
    arc(d, d.r * 0.6, -d.r * 0.6, d.r * 0.3, false);
    arc(d, d.r * 0.6, d.r * 0.6, d.r * 0.3, false);
  },
  pout: (d) => {
    bar(d, d.r * 1.2, 0, -d.r * 0.1);
    arc(d, d.r * 0.5, 0, d.r * 0.4, false);
  },
};

/** Add the mouth to the face group. */
export function addMouth(face: Group, kind: SigmaBotBody, b: BodyDef, style: SigmaBotMouth): void {
  const y = -b.mouthY;
  const mouth = new Group();
  mouth.name = "SigmaBotMouth";
  mouth.position.set(0, y, frontZ(kind, y, 0));
  MOUTH_STYLES[style]({ g: mouth, r: b.eyeR, z: b.eyeR * 0.1 });
  face.add(mouth);
}
